import { useEffect, useState } from "react";

import { api, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { navigate } from "../router";

/**
 * The unread badge in the header.
 *
 * A button rather than the dropdown this replaces. A dropdown was fine while
 * mentions were the only alert; with work arriving and assignments in there
 * too it becomes a list things scroll out of, and there is nowhere to keep
 * what has already been read.
 *
 * Refetched on a timer as well as on demand, because the whole point of the
 * count is telling someone about something they did not do themselves — and
 * during a handover the two people are looking at two different screens.
 */
export function InboxButton({
  fetcher,
  refreshKey,
}: {
  fetcher: Fetcher;
  /** Bumped by the app when something happened that may change the count. */
  refreshKey: number;
}) {
  const [unread, setUnread] = useState(0);
  // And again on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn({ whileEditing: true });

  useEffect(() => {
    let cancelled = false;

    const load = (): void => {
      void api
        .inboxUnread(fetcher)
        .then((r) => {
          if (cancelled) return;
          setUnread(r.unread);
        })
        .catch(() => undefined);
    };

    load();
    // Half a minute: often enough that a handover feels live, rare enough
    // that it is one small query per person per minute or so.
    const timer = window.setInterval(load, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [fetcher, refreshKey, back]);

  return (
    <button
      className={unread > 0 ? "report-open inbox-btn has-unread" : "report-open inbox-btn"}
      onClick={() => navigate({ kind: "inbox" })}
    >
      Inbox
      {unread > 0 ? <span className="badge">{unread}</span> : null}
    </button>
  );
}
